import React, { useState, useEffect } from "react"
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Tabs, Tab, MenuItem, TextField, Chip, CircularProgress
} from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs"
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro"
import dayjs from "dayjs"
import SlotPicker from "./SlotPicker"
import { BookSlot, UnbookSlot, GetNextSlotDate, BookModalitySlot, UnbookModalitySlot, GetModalitySchedule } from "../../../api"
import { MODALITY_OPTIONS } from "../../../constants/Consult"

const ScheduleSlotDialog = ({ open, onClose, study, access }) => {
    const orgId = access?.org?.id
    const isParent = !!access?.org?.settings?.scheduler
    const hasSchedule = !!(access?.org?.settings?.enableScheduling || access?.org?.settings?.scheduler)
    const hasModalitySchedule = !!access?.org?.settings?.modalityScheduler

    // Determine initial tab: if already booked on modality scheduler go to that tab, else default to slot tab if available
    const getInitialTab = () => {
        if (study?.modalitySlot?.slotId) return 'modality'
        if (study?.scheduleSlot?.slotId) return 'slot'
        return hasSchedule ? 'slot' : 'modality'
    }

    const showTabs = hasSchedule && hasModalitySchedule
    const [tab, setTab] = useState('slot')

    // --- Slot tab state ---
    const [slotDate, setSlotDate] = useState(null)
    const [selectedSlot, setSelectedSlot] = useState(null)

    // --- Modality tab state ---
    const [modDate, setModDate] = useState(dayjs().format('YYYY-MM-DD'))
    const [modality, setModality] = useState('')
    const [startTime, setStartTime] = useState('09:00')
    const [endTime, setEndTime] = useState('09:30')
    const [modCapacity, setModCapacity] = useState(null) // { count, max }
    const [modCapLoading, setModCapLoading] = useState(false)

    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) { setSlotDate(null); setSelectedSlot(null); setModCapacity(null); return }
        const initialTab = getInitialTab()
        setTab(initialTab)

        // Slot tab init
        if (hasSchedule) {
            if (study?.scheduleSlot?.date) {
                setSlotDate(study.scheduleSlot.date.slice(0, 10))
            } else {
                setSlotDate(null)
                const childOrg = !isParent ? orgId : undefined
                GetNextSlotDate(orgId, childOrg)
                    .then(r => setSlotDate(r?.date || dayjs().format('YYYY-MM-DD')))
                    .catch(() => setSlotDate(dayjs().format('YYYY-MM-DD')))
            }
        }

        // Modality tab init
        if (hasModalitySchedule) {
            if (study?.modalitySlot?.date) {
                setModDate(study.modalitySlot.date.slice(0, 10))
            } else {
                setModDate(dayjs().format('YYYY-MM-DD'))
            }
            setModality('')
            setStartTime('09:00')
            setEndTime('09:30')
        }

        setSelectedSlot(null)
        setModCapacity(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, study?.id])

    // Fetch modality capacity when date or modality changes on modality tab
    useEffect(() => {
        if (!open || tab !== 'modality' || !modDate || !modality) { setModCapacity(null); return }
        setModCapLoading(true)
        GetModalitySchedule(orgId, modDate)
            .then(r => {
                const count = r.countByModality?.[modality] || 0
                const limit = r.limits?.[modality]
                setModCapacity({ count, max: limit?.max ?? null })
            })
            .catch(() => setModCapacity(null))
            .finally(() => setModCapLoading(false))
    }, [open, tab, modDate, modality, orgId])

    const handleSaveSlot = async () => {
        if (!selectedSlot) return
        setSaving(true)
        try {
            await BookSlot({ date: slotDate, templateId: selectedSlot.templateId, slotId: selectedSlot.slotId, caseId: study.id })
            onClose(true)
        } catch (e) {
        } finally { setSaving(false) }
    }

    const handleUnbookSlot = async () => {
        setSaving(true)
        try {
            await UnbookSlot({ caseId: study.id })
            onClose(true)
        } catch (e) {
        } finally { setSaving(false) }
    }

    const handleSaveModality = async () => {
        if (!modality || !modDate || !startTime || !endTime) return
        setSaving(true)
        try {
            await BookModalitySlot(orgId, { studyId: study.id, modality, date: modDate, startTime, endTime })
            onClose(true)
        } catch (e) {
        } finally { setSaving(false) }
    }

    const handleUnbookModality = async () => {
        setSaving(true)
        try {
            await UnbookModalitySlot(orgId, { studyId: study.id })
            onClose(true)
        } catch (e) {
        } finally { setSaving(false) }
    }

    const modalityFull = modCapacity?.max != null && modCapacity.count >= modCapacity.max

    if (!study) return null

    return (
        <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
            <DialogTitle>
                Schedule — {study.PatientMainDicomTags?.PatientName || 'Study'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    {showTabs && (
                        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                            <Tab label="Slot Scheduler" value="slot" />
                            <Tab label="Modality Scheduler" value="modality" />
                        </Tabs>
                    )}

                    {/* --- Slot tab --- */}
                    {tab === 'slot' && (
                        <>
                            {study.scheduleSlot?.slotId && (
                                <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">Currently booked (slot scheduler)</Typography>
                                    <Typography variant="body2">
                                        {dayjs(study.scheduleSlot.date.slice(0, 10)).format('MM/DD/YYYY')}
                                    </Typography>
                                </Box>
                            )}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Select Date"
                                    value={slotDate ? dayjs(slotDate) : null}
                                    onChange={v => { if (v) { setSlotDate(v.format('YYYY-MM-DD')); setSelectedSlot(null) } }}
                                    sx={{ width: '100%', mb: 2 }}
                                />
                            </LocalizationProvider>
                            {slotDate && (
                                <SlotPicker
                                    orgId={orgId}
                                    date={slotDate}
                                    selectedSlot={selectedSlot}
                                    onSelect={setSelectedSlot}
                                    filterChildOrg={!isParent ? orgId : undefined}
                                    isParent={isParent}
                                />
                            )}
                        </>
                    )}

                    {/* --- Modality tab --- */}
                    {tab === 'modality' && (
                        <>
                            {study.modalitySlot?.slotId && (
                                <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">Currently booked (modality scheduler)</Typography>
                                    <Typography variant="body2">
                                        {dayjs(study.modalitySlot.date.slice(0, 10)).format('MM/DD/YYYY')}
                                    </Typography>
                                </Box>
                            )}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Select Date"
                                    value={modDate ? dayjs(modDate) : null}
                                    onChange={v => { if (v) setModDate(v.format('YYYY-MM-DD')) }}
                                    sx={{ width: '100%', mb: 2 }}
                                />
                            </LocalizationProvider>
                            <TextField
                                select
                                label="Modality"
                                value={modality}
                                onChange={e => setModality(e.target.value)}
                                fullWidth
                                sx={{ mb: 2 }}
                            >
                                {MODALITY_OPTIONS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                            </TextField>
                            {modality && modDate && (
                                <>
                                    {modCapLoading
                                        ? <Box sx={{ mb: 1 }}><CircularProgress size={16} /></Box>
                                        : modCapacity && (
                                            <Chip size="small" sx={{ mb: 2 }}
                                                label={`${modality}: ${modCapacity.count}/${modCapacity.max ?? '∞'} scheduled`}
                                                color={modalityFull ? 'error' : 'default'}
                                            />
                                        )
                                    }
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <TimePicker label="Start Time"
                                                value={dayjs(`2000-01-01T${startTime}`)}
                                                onChange={v => {
                                                    if (!v) return
                                                    const s = v.format('HH:mm')
                                                    setStartTime(s)
                                                    const endMins = v.hour() * 60 + v.minute() + 30
                                                    setEndTime(`${String(Math.floor(endMins / 60)).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`)
                                                }}
                                                sx={{ flex: 1 }}
                                            />
                                            <TimePicker label="End Time"
                                                value={dayjs(`2000-01-01T${endTime}`)}
                                                onChange={v => v && setEndTime(v.format('HH:mm'))}
                                                sx={{ flex: 1 }}
                                            />
                                        </Box>
                                    </LocalizationProvider>
                                </>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                {tab === 'slot' && study.scheduleSlot?.slotId && (
                    <Button color="error" onClick={handleUnbookSlot} disabled={saving}>Unschedule</Button>
                )}
                {tab === 'modality' && study.modalitySlot?.slotId && (
                    <Button color="error" onClick={handleUnbookModality} disabled={saving}>Unschedule</Button>
                )}
                <Button onClick={() => onClose(false)}>Cancel</Button>
                {tab === 'slot' && (
                    <Button onClick={handleSaveSlot} variant="contained" disabled={!selectedSlot || saving}>
                        {study.scheduleSlot?.slotId ? 'Reschedule' : 'Schedule'}
                    </Button>
                )}
                {tab === 'modality' && (
                    <Button onClick={handleSaveModality} variant="contained"
                        disabled={!modality || !modDate || !startTime || !endTime || modalityFull || saving}>
                        {study.modalitySlot?.slotId ? 'Reschedule' : 'Schedule'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default ScheduleSlotDialog
